import { apiFetch } from '@/lib/api-client';

export type ApiEnvelope<T> =
  | { ok: true; data: T }
  | {
      ok: false;
      error: { code: string; message: string; field?: string };
    };

export interface CreateCompanyInput {
  name: string;
  invited_emails: string[];
  contact_email?: string;
  contact_name?: string;
}

interface RawCreateCompanyData {
  company_id?: string;
  company?: { id?: string };
}

type RawCreateCompanyEnvelope = ApiEnvelope<RawCreateCompanyData>;

function normalizeCreateCompanyEnvelope(
  envelope: RawCreateCompanyEnvelope,
): ApiEnvelope<{ company_id: string }> {
  if (!envelope.ok) {
    return envelope;
  }

  const companyId = envelope.data.company_id ?? envelope.data.company?.id;
  if (!companyId) {
    return {
      ok: false,
      error: {
        code: 'parse_error',
        message: 'missing company_id in create-company response',
      },
    };
  }

  return { ok: true, data: { company_id: companyId } };
}

export async function createCompany(
  input: CreateCompanyInput,
): Promise<ApiEnvelope<{ company_id: string }>> {
  const res = await apiFetch('/api/companies', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(input),
  });

  const text = await res.text();
  try {
    const parsed = JSON.parse(text) as RawCreateCompanyEnvelope;
    return normalizeCreateCompanyEnvelope(parsed);
  } catch {
    // Server returned non-JSON (typically HTML 404 / 500 page when an API
    // route is unmounted or the proxy upstream is down). Never surface the
    // raw body — it leaks HTML markup into the UI. Return a neutral
    // user-facing message; devtools Network tab carries the raw evidence.
    return {
      ok: false,
      error: {
        code: 'unexpected_response',
        message:
          'Something went wrong on our side. Please try again, or refresh the page if it keeps happening.',
      },
    };
  }
}
